import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';
import Modal from '../common/Modal';
import ThemeSwitcher from '../common/ThemeSwitcher';
import TeamSelector from '../TeamSelector';
import './Header.css';

/**
 * Header Component
 * 
 * Application header with logo and user info
 */
export default function Header() {
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();

  // Close dropdown on route change
  useEffect(() => {
    setShowUserMenu(false);
  }, [location.pathname]);

  const getUserInitials = () => {
    if (user?.attributes?.name) {
      const names = user.attributes.name.split(' ');
      return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getUserName = () => {
    if (user?.attributes?.name) return user.attributes.name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const getRoleLabel = () => {
    const role = user?.role;
    if (role === 'admin') return 'Admin';
    if (role === 'manager') return 'Manager';
    return 'Viewer';
  };

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-logo">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <span className="header-logo-text">TUI ReportHub</span>
          {config.app.environment !== 'prod' && (
            <span className="env-badge">{config.app.environment}</span>
          )}
        </div>

        <div className="header-actions">
          <TeamSelector />
          <ThemeSwitcher />
          <div className="user-menu">
            <button
              className="user-menu-trigger"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
              <div className="user-avatar">{getUserInitials()}</div>
              <span className="user-name">{getUserName()}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showUserMenu && (
              <>
                <div className="user-menu-backdrop" onClick={() => setShowUserMenu(false)} />
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">
                    <div className="user-menu-avatar">{getUserInitials()}</div>
                    <div className="user-menu-info">
                      <p className="user-menu-name">{getUserName()}</p>
                      <p className="user-menu-email">{user?.email}</p>
                      <p className="user-menu-role">{getRoleLabel()}</p>
                    </div>
                  </div>
                  <div className="user-menu-divider" />
                  <button
                    className="user-menu-item user-menu-logout"
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowLogoutModal(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Log Out"
        size="sm"
        footer={
          <div className="logout-modal-actions">
            <button className="btn btn--ghost btn--sm" onClick={() => setShowLogoutModal(false)}>
              Cancel
            </button>
            <button className="btn btn--danger btn--sm" onClick={logout}>
              Log Out
            </button>
          </div>
        }
      >
        <p className="logout-modal-message">
          You will be logged out of ReportHub. You may need to re-authenticate with other TUI applications.
        </p>
      </Modal>
    </header>
  );
}
